import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { metricsService } from '../services/domain';

// Individual Health Issue Item
function HealthIssueItem({ issue, onAction, expanded, onToggleExpand }) {
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getSeverityBgColor = (severity) => {
    switch (severity) {
      case 'error': return 'error.lighter';
      case 'warning': return 'warning.lighter';
      case 'info': return 'info.lighter';
      default: return 'grey.100';
    }
  };

  return (
    <Box mb={2}>
      <Box 
        p={2} 
        sx={{ 
          backgroundColor: getSeverityBgColor(issue.severity), 
          borderRadius: 1,
          cursor: issue.details ? 'pointer' : 'default'
        }}
        onClick={() => issue.details && onToggleExpand(issue.id)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="flex-start" flex={1}>
            <Icon 
              color={getSeverityColor(issue.severity)} 
              sx={{ mr: 2, mt: 0.5 }}
            >
              {issue.icon}
            </Icon>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="button" fontWeight="medium">
                  {issue.title}
                </Typography>
                {issue.totalCount > 5 && (
                  <Chip 
                    label={`${issue.totalCount} total`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20 }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text" display="block">
                {issue.description}
              </Typography>
              
              {/* Expandable Details */}
              {issue.details && (
                <Collapse in={expanded}>
                  <Box mt={2} pl={2} borderLeft="2px solid" borderColor="divider">
                    {issue.details.map((detail, idx) => (
                      <Box key={idx} py={0.5}>
                        <Typography variant="caption" fontWeight="medium">
                          {detail.name || detail.locationNumber || `Item ${idx + 1}`}
                        </Typography>
                        {detail.issue && (
                          <Typography variant="caption" color="text" display="block">
                            {detail.issue}
                          </Typography>
                        )}
                      </Box>
                    ))}
                    {issue.totalCount > issue.details.length && (
                      <Typography variant="caption" color="text" fontStyle="italic" display="block" mt={1}>
                        ...and {issue.totalCount - issue.details.length} more
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              )}
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            {issue.details && (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleExpand(issue.id); }}>
                <Icon fontSize="small">
                  {expanded ? 'expand_less' : 'expand_more'}
                </Icon>
              </IconButton>
            )}
            <AdminButton 
              variant="text" 
              size="small" 
              color="dark"
              onClick={(e) => { e.stopPropagation(); onAction(issue); }}
            >
              {issue.action}
            </AdminButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Health Score Indicator
function HealthScoreIndicator({ score, label }) {
  const getScoreColor = () => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getScoreIcon = () => {
    if (score >= 90) return 'check_circle';
    if (score >= 70) return 'warning';
    return 'error';
  };

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box position="relative" display="inline-flex">
        <CircularProgress 
          variant="determinate" 
          value={score} 
          size={60}
          thickness={4}
          color={getScoreColor()}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon color={getScoreColor()} fontSize="small">
            {getScoreIcon()}
          </Icon>
        </Box>
      </Box>
      <Box>
        <Typography variant="h4" fontWeight="bold">
          {score}%
        </Typography>
        <Typography variant="caption" color="text">
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

// Passed Check Item
function PassedCheckItem({ check }) {
  return (
    <Box 
      display="flex" 
      alignItems="center" 
      py={0.5}
      px={1}
      sx={{
        borderRadius: 1,
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
    >
      <Icon color="success" sx={{ mr: 1.5, fontSize: 20 }}>
        check_circle
      </Icon>
      <Typography variant="caption" fontWeight="medium" color="text">
        {check.title}
      </Typography>
    </Box>
  );
}

// Main System Health Card Component
function SystemHealthCard({ clientId, onNavigate, healthData: propHealthData, loading: propLoading }) {
  const [localLoading, setLocalLoading] = useState(!propHealthData);
  const [localHealthData, setLocalHealthData] = useState(propHealthData || null);
  const [error, setError] = useState(null);
  const [expandedIssues, setExpandedIssues] = useState(new Set());
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Use prop data if provided, otherwise load locally
  const loading = propLoading !== undefined ? propLoading : localLoading;
  const healthData = propHealthData || localHealthData;

  useEffect(() => {
    // Only load data if not provided via props
    if (!propHealthData && clientId) {
      loadHealthData();
    }
  }, [clientId, propHealthData]);
  
  // Update local state when prop data changes
  useEffect(() => {
    if (propHealthData) {
      setLocalHealthData(propHealthData);
      setLocalLoading(false);
    }
  }, [propHealthData]);

  const loadHealthData = async () => {
    try {
      setLocalLoading(true);
      setError(null);
      const data = await metricsService.getSystemHealth(clientId);
      setLocalHealthData(data);
    } catch (err) {
      console.error('Error loading health data:', err);
      setError('Failed to load system health data');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // If data is passed as prop, we can't refresh locally
    // In a real app, you'd call a parent callback to refresh
    if (!propHealthData) {
      await loadHealthData();
    }
    setRefreshing(false);
  };

  const toggleIssueExpand = (issueId) => {
    setExpandedIssues(prev => {
      const newSet = new Set(prev);
      if (newSet.has(issueId)) {
        newSet.delete(issueId);
      } else {
        newSet.add(issueId);
      }
      return newSet;
    });
  };

  const handleIssueAction = (issue) => {
    // Navigate based on issue type
    switch (issue.category) {
      case 'location-assignment':
        onNavigate('locations');
        break;
      case 'manager-assignment':
        // Check if it's specifically for locations
        if (issue.id === 'locations-without-managers') {
          onNavigate('locations');
        } else {
          onNavigate('units');
        }
        break;
      case 'hierarchy-structure':
        onNavigate('units');
        break;
      case 'data-integrity':
        onNavigate('locations');
        break;
      default:

    }
  };

  if (loading && !refreshing) {
    return (
      <Card>
        <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Box p={3}>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={loadHealthData}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </Box>
      </Card>
    );
  }

  if (!healthData) {
    return null;
  }

  const displayedIssues = showAllIssues ? healthData.issues : healthData.issues.slice(0, 3);
  const hasMoreIssues = healthData.issues.length > 3;

  return (
    <Card>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" fontWeight="medium">
              Location Hierarchy Health
            </Typography>
            <Typography variant="caption" color="text" display="block">
              Automated checks for data integrity and completeness
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh health checks">
              <IconButton 
                size="small" 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <Icon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>
                  refresh
                </Icon>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Health Score */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <HealthScoreIndicator 
            score={healthData.overallCompletion} 
            label="Overall Health"
          />
          <Box display="flex" gap={2}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="error">
                {healthData.metrics.errors}
              </Typography>
              <Typography variant="caption" color="text">
                Critical
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="warning">
                {healthData.metrics.warnings}
              </Typography>
              <Typography variant="caption" color="text">
                Warnings
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="success">
                {healthData.metrics.passedChecks}
              </Typography>
              <Typography variant="caption" color="text">
                Passed
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Issues */}
        {healthData.issues.length > 0 ? (
          <>
            <Typography variant="button" fontWeight="medium" color="text" display="block" mb={2}>
              Issues to Address ({healthData.issues.length})
            </Typography>
            
            {displayedIssues.map((issue) => (
              <HealthIssueItem
                key={issue.id}
                issue={issue}
                expanded={expandedIssues.has(issue.id)}
                onToggleExpand={toggleIssueExpand}
                onAction={handleIssueAction}
              />
            ))}
            
            {hasMoreIssues && (
              <Box display="flex" justifyContent="center" mt={2}>
                <AdminButton
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={() => setShowAllIssues(!showAllIssues)}
                >
                  {showAllIssues ? 'Show Less' : `Show ${healthData.issues.length - 3} More Issues`}
                </AdminButton>
              </Box>
            )}
          </>
        ) : (
          <Box p={3} textAlign="center" sx={{ backgroundColor: 'success.lighter', borderRadius: 1 }}>
            <Icon color="success" sx={{ fontSize: 48, mb: 1 }}>
              check_circle
            </Icon>
            <Typography variant="h6" fontWeight="medium" color="success">
              All Systems Healthy
            </Typography>
            <Typography variant="caption" color="text">
              No issues detected in your location hierarchy
            </Typography>
          </Box>
        )}

        {/* Passed Checks */}
        {healthData.passedChecks && healthData.passedChecks.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="button" fontWeight="medium" color="text" display="block" mb={2}>
              Checks Passed ({healthData.passedChecks.length})
            </Typography>
            <Box>
              {healthData.passedChecks.map((check) => (
                <PassedCheckItem key={check.id} check={check} />
              ))}
            </Box>
          </>
        )}

      </Box>
    </Card>
  );
}

export default SystemHealthCard;